import React, { useState, useMemo, useEffect } from 'react';
import {
  Card, Button, Table, Modal, Form, Input, Select, Space, Tag,
  Badge, Row, Col, Statistic, Typography, Timeline, Alert,
  Divider, Popconfirm, Tooltip, Progress, InputNumber,
} from 'antd';
import {
  PlusOutlined, ToolOutlined, CheckCircleOutlined,
  SearchOutlined, EditOutlined, DeleteOutlined,
  EyeOutlined, FileAddOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useCondoStore } from '../store/condoStore';
import { useAuthStore } from '../store/authStore';
import { MaintenanceRequest } from '../types';
import { staticApi } from '../utils/staticApi';
import { resolveOwnerRecordKey } from '../utils/resolveOwnerKeys';
import { generateMaintenanceRequestNumber } from '../utils/requestNumber';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

type RequestStatus = 'submitted' | 'in_review' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
type RequestPriority = 'low' | 'medium' | 'high' | 'urgent';

const CATEGORIES = [
  'Plumbing', 'Electrical', 'HVAC / Heating', 'Elevator', 'Common Area',
  'Landscaping', 'Pest Control', 'Appliance', 'Structural', 'Security', 'Other',
];

const VENDORS = [
  'ABC Plumbing Inc.', 'QuickFix Electric', 'Cool Air HVAC', 'GreenScape Landscaping',
  'ProPest Solutions', 'Reliable Contractors Ltd.', 'City Elevator Services', 'Internal Maintenance',
];

const STATUS_CONFIG: Record<RequestStatus, { color: string; label: string; step: number }> = {
  submitted:   { color: 'default',    label: 'Submitted',    step: 0 },
  in_review:   { color: 'processing', label: 'In Review',    step: 1 },
  assigned:    { color: 'blue',       label: 'Assigned',     step: 2 },
  in_progress: { color: 'warning',    label: 'In Progress',  step: 3 },
  completed:   { color: 'success',    label: 'Completed',    step: 4 },
  cancelled:   { color: 'error',      label: 'Cancelled',    step: 4 },
};

const PRIORITY_CONFIG: Record<RequestPriority, { color: string; label: string }> = {
  low:    { color: 'green',  label: 'Low' },
  medium: { color: 'blue',   label: 'Medium' },
  high:   { color: 'orange', label: 'High' },
  urgent: { color: 'red',    label: 'Urgent' },
};

// Sprint 147 (R14-24) — single shared generator; the store slice used to mint
// `MR-<epoch>` for the same list. See utils/requestNumber.ts.
const genRequestNumber = generateMaintenanceRequestNumber;

export const MaintenanceRequests: React.FC = () => {
  // Sprint 5e — narrow selectors instead of whole-store destructure.
  const owners                       = useCondoStore((s: any) => s.owners);
  const requests                     = useCondoStore((s: any) => s.maintenanceRequests);
  const fetchMaintenanceRequests     = useCondoStore((s: any) => s.fetchMaintenanceRequests);
  const createMaintenanceRequest     = useCondoStore((s: any) => s.createMaintenanceRequest);
  const updateMaintenanceRequest     = useCondoStore((s: any) => s.updateMaintenanceRequest);
  const deleteMaintenanceRequest     = useCondoStore((s: any) => s.deleteMaintenanceRequest);
  const updateMaintenanceRequestStatus = useCondoStore((s: any) => s.updateMaintenanceRequestStatus);
  const createWorkOrder              = useCondoStore((s: any) => s.createWorkOrder);
  const createWorkOrderFromRequest   = useCondoStore((s: any) => s.createWorkOrderFromRequest);
  const { currentCondo } = useAuthStore();
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchMaintenanceRequests(currentCondo?.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetchMaintenanceRequests is a stable zustand action
  }, [currentCondo?.id]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<MaintenanceRequest | null>(null);
  const [editing, setEditing] = useState<MaintenanceRequest | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [noteText, setNoteText] = useState('');
  const [form] = Form.useForm();
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalatingRequest, setEscalatingRequest] = useState<MaintenanceRequest | null>(null);
  const [escalateForm] = Form.useForm();

  const filtered = useMemo(() => requests.filter((r: MaintenanceRequest) => {
    const matchText = !search ||
      r.requestNumber.toLowerCase().includes(search.toLowerCase()) ||
      r.unitNumber.toLowerCase().includes(search.toLowerCase()) ||
      r.ownerName.toLowerCase().includes(search.toLowerCase()) ||
      r.title.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !filterStatus || r.status === filterStatus;
    const matchPriority = !filterPriority || r.priority === filterPriority;
    return matchText && matchStatus && matchPriority;
  }), [requests, search, filterStatus, filterPriority]);

  const stats = useMemo(() => ({
    total: requests.length,
    open: requests.filter((r: MaintenanceRequest) => !['completed', 'cancelled'].includes(r.status)).length,
    urgent: requests.filter((r: MaintenanceRequest) => r.priority === 'urgent' && r.status !== 'completed').length,
    completed: requests.filter((r: MaintenanceRequest) => r.status === 'completed').length,
    completionRate: requests.length > 0
      ? Math.round((requests.filter((r: MaintenanceRequest) => r.status === 'completed').length / requests.length) * 100)
      : 0,
  }), [requests]);

  const handleOpen = (r?: MaintenanceRequest) => {
    setEditing(r ?? null);
    if (r) {
      form.setFieldsValue(r);
    } else {
      form.resetFields();
      form.setFieldsValue({
        requestNumber: genRequestNumber(),
        submittedDate: dayjs().format('YYYY-MM-DD'),
        targetDate: dayjs().add(7, 'day').format('YYYY-MM-DD'),
        status: 'submitted',
        priority: 'medium',
      });
    }
    setModalOpen(true);
  };

  // Sprint 136 (ADR-131) — Select value is the canonical owner key; look up by it.
  const handleOwnerSelect = (ownerId: string) => {
    const owner = owners.find((o: any) => resolveOwnerRecordKey(o) === ownerId);
    if (owner) form.setFieldsValue({ unitNumber: owner.unitNumber, ownerName: owner.name });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const normalized = {
        ...values,
        status: values.status?.toUpperCase(),
        priority: values.priority?.toUpperCase(),
      };
      if (editing) {
        await updateMaintenanceRequest(editing.id, { ...normalized, condoId: currentCondo?.id || editing.condoId || '' });
        staticApi.message.success('Request updated');
      } else {
        await createMaintenanceRequest({
          notes: [],
          condoId: currentCondo?.id || '',
          ...normalized,
        });
        staticApi.message.success(`Request ${values.requestNumber} submitted`);
      }
      setModalOpen(false);
    } catch { /* validation */ }
  };

  const handleStatusChange = async (r: MaintenanceRequest, status: RequestStatus) => {
    try {
      await updateMaintenanceRequestStatus(r.id, status.toUpperCase() as RequestStatus);
      if (selected?.id === r.id) setSelected(requests.find((x: MaintenanceRequest) => x.id === r.id) ?? null);
      staticApi.message.success(`Status updated to ${STATUS_CONFIG[status].label}`);
    } catch {
      staticApi.message.error('Failed to update status');
    }
  };

  const handleAddNote = async (r: MaintenanceRequest) => {
    if (!noteText.trim()) return;
    try {
      await updateMaintenanceRequest(r.id, {
        notes: [...r.notes, { id: `n-${Date.now()}`, date: dayjs().toISOString(), author: 'Manager', text: noteText.trim() }],
      });
      const updated = requests.find((x: MaintenanceRequest) => x.id === r.id);
      if (updated) setSelected(updated);
      setNoteText('');
    } catch {
      staticApi.message.error('Failed to add note');
    }
  };

  const PRIORITY_WO_MAP: Record<string, 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'> = {
    low: 'LOW', medium: 'MEDIUM', high: 'HIGH', urgent: 'URGENT',
  };

  const handleEscalate = (r: MaintenanceRequest) => {
    setEscalatingRequest(r);
    escalateForm.setFieldsValue({
      title: r.title,
      description: r.description,
      unitNumber: r.unitNumber,
      priority: PRIORITY_WO_MAP[r.priority?.toLowerCase()] ?? 'MEDIUM',
      vendorName: r.assignedVendor ?? '',
      estimatedCost: r.estimatedCost ?? undefined,
    });
    setEscalateOpen(true);
  };

  const handleEscalateSubmit = async () => {
    try {
      const values = await escalateForm.validateFields();
      await createWorkOrder({
        condoId:              currentCondo?.id || '',
        maintenanceRequestId: escalatingRequest?.id,
        title:                values.title,
        description:          values.description,
        unitNumber:           values.unitNumber,
        priority:             values.priority,
        vendorName:           values.vendorName || undefined,
        vendorContact:        values.vendorContact || undefined,
        estimatedCost:        values.estimatedCost ?? undefined,
        scheduledDate:        values.scheduledDate || undefined,
        notes:                values.notes || undefined,
        status:               'OPEN',
        createdAt:            new Date().toISOString(),
      });
      staticApi.message.success(`Work order created from request ${escalatingRequest?.requestNumber}`);
      setEscalateOpen(false);
      escalateForm.resetFields();
    } catch {
      staticApi.message.error('Failed to create work order');
    }
  };

  const columns = [
    { title: 'Request No.', dataIndex: 'requestNumber', key: 'num', render: (v: string) => <Tag color="cyan">{v}</Tag> },
    { title: 'Unit', dataIndex: 'unitNumber', key: 'unit', render: (v: string) => <strong>Unit {v}</strong> },
    { title: 'Owner', dataIndex: 'ownerName', key: 'owner' },
    { title: 'Title', dataIndex: 'title', key: 'title' },
    {
      // Sprint 38 — finding H4. Round-5 saw mixed casing in this
      // column ("Plumbing" from the seeded Sprint-33 row, "PLUMBING"
      // from the older mock). Backend stores categories as uppercase
      // enums; normalise to Title Case on render so the column reads
      // consistently regardless of the data source.
      title: 'Category', dataIndex: 'category', key: 'cat',
      render: (v: string) => {
        if (!v) return <Tag>—</Tag>;
        const pretty = v.toLowerCase().replace(/(^|[_\s-])(\w)/g, (_, sep, c) =>
          (sep ? ' ' : '') + c.toUpperCase());
        return <Tag>{pretty}</Tag>;
      },
    },
    {
      title: 'Priority', dataIndex: 'priority', key: 'pri',
      render: (v: RequestPriority) => <Tag color={PRIORITY_CONFIG[v].color}>{PRIORITY_CONFIG[v].label}</Tag>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: (v: RequestStatus) => <Badge status={STATUS_CONFIG[v].color as any} text={STATUS_CONFIG[v].label} />,
    },
    { title: 'Submitted', dataIndex: 'submittedDate', key: 'date', render: (v: string) => dayjs(v).format('MMM D, YYYY') },
    { title: 'Target', dataIndex: 'targetDate', key: 'target', render: (v: string) => {
      const overdue = dayjs(v).isBefore(dayjs());
      return <Text type={overdue ? 'danger' : undefined}>{dayjs(v).format('MMM D, YYYY')}</Text>;
    }},
    {
      title: 'Actions', key: 'actions',
      render: (_: any, r: MaintenanceRequest) => (
        <Space>
          <Tooltip title="View"><Button size="small" icon={<EyeOutlined />} onClick={() => { setSelected(r); setDetailOpen(true); }} /></Tooltip>
          <Tooltip title="Edit"><Button size="small" icon={<EditOutlined />} onClick={() => handleOpen(r)} /></Tooltip>
          {!['completed', 'cancelled'].includes(r.status) && (
            <>
              <Tooltip title="Quick: auto-create Work Order from this request">
                <Button
                  size="small"
                  icon={<ThunderboltOutlined />}
                  style={{ color: '#13c2c2', borderColor: '#13c2c2' }}
                  onClick={async () => {
                    try {
                      const wo = await createWorkOrderFromRequest(r.id);
                      staticApi.message.success(`Work Order created: ${wo.title}`);
                    } catch (err: any) {
                      staticApi.message.error(err?.message || 'Work order already exists or failed');
                    }
                  }}
                />
              </Tooltip>
              <Tooltip title="Escalate to Work Order (manual)">
                <Button size="small" icon={<FileAddOutlined />} style={{ color: '#722ed1', borderColor: '#722ed1' }} onClick={() => handleEscalate(r)} />
              </Tooltip>
            </>
          )}
          <Popconfirm title="Delete request?" onConfirm={() => deleteMaintenanceRequest(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          <ToolOutlined style={{ marginRight: 10, color: '#722ed1' }} />
          Maintenance Requests
        </Title>
        <Text type="secondary">Track and manage all maintenance and repair requests from residents</Text>
      </div>

      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col span={5}>
          <Card><Statistic title="Total Requests" value={stats.total} prefix={<ToolOutlined />} /></Card>
        </Col>
        <Col span={5}>
          <Card><Statistic title="Open" value={stats.open} valueStyle={{ color: '#fa8c16' }} /></Card>
        </Col>
        <Col span={5}>
          <Card><Statistic title="Urgent" value={stats.urgent} valueStyle={{ color: '#ff4d4f' }} /></Card>
        </Col>
        <Col span={5}>
          <Card><Statistic title="Completed" value={stats.completed} valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
        <Col span={4}>
          <Card>
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Completion Rate</Text>
              <div style={{ marginTop: 4 }}>
                <Progress type="circle" percent={stats.completionRate} width={56} strokeColor="#52c41a" />
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <Card
        title="All Maintenance Requests"
        extra={
          <Space>
            <Input placeholder="Search…" prefix={<SearchOutlined />} value={search} onChange={e => setSearch(e.target.value)} style={{ width: 200 }} allowClear />
            <Select placeholder="Status" allowClear style={{ width: 140 }} value={filterStatus || undefined} onChange={v => setFilterStatus(v ?? '')}>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => <Option key={k} value={k}>{v.label}</Option>)}
            </Select>
            <Select placeholder="Priority" allowClear style={{ width: 120 }} value={filterPriority || undefined} onChange={v => setFilterPriority(v ?? '')}>
              {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <Option key={k} value={k}>{v.label}</Option>)}
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpen()}>New Request</Button>
          </Space>
        }
      >
        <Table columns={columns} dataSource={filtered} rowKey="id" pagination={{ pageSize: 10 }} scroll={{ x: 1100 }} />
      </Card>

      {/* Create / Edit Modal */}
      <Modal open={modalOpen} onCancel={() => setModalOpen(false)} title={editing ? 'Edit Request' : 'New Maintenance Request'} onOk={handleSubmit} width={680} okText={editing ? 'Save' : 'Submit Request'}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="requestNumber" label="Request Number" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}>
              <Form.Item name="ownerId" label="Owner / Unit">
                <Select showSearch placeholder="Select owner" optionFilterProp="label" onChange={handleOwnerSelect}
                  options={owners.map((o: any) => ({ label: `Unit ${o.unitNumber} — ${o.name}`, value: resolveOwnerRecordKey(o) }))} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="unitNumber" label="Unit Number" rules={[{ required: true }]}><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="ownerName" label="Resident Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="title" label="Request Title" rules={[{ required: true }]}><Input placeholder="Brief description of the issue" /></Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                <Select>{CATEGORIES.map(c => <Option key={c} value={c}>{c}</Option>)}</Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="priority" label="Priority" rules={[{ required: true }]}>
                <Select>{Object.entries(PRIORITY_CONFIG).map(([k, v]) => <Option key={k} value={k}><Tag color={v.color}>{v.label}</Tag></Option>)}</Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Detailed Description" rules={[{ required: true }]}><TextArea rows={3} /></Form.Item>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="submittedDate" label="Date Submitted" rules={[{ required: true }]}><Input type="date" /></Form.Item></Col>
            <Col span={8}><Form.Item name="targetDate" label="Target Date" rules={[{ required: true }]}><Input type="date" /></Form.Item></Col>
            <Col span={8}>
              <Form.Item name="status" label="Status">
                <Select>{Object.entries(STATUS_CONFIG).map(([k, v]) => <Option key={k} value={k}>{v.label}</Option>)}</Select>
              </Form.Item>
            </Col>
          </Row>
          <Divider plain>Assignment & Cost (optional)</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="assignedVendor" label="Assigned Vendor">
                <Select showSearch allowClear placeholder="Select or type vendor">
                  {VENDORS.map(v => <Option key={v} value={v}>{v}</Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}><Form.Item name="estimatedCost" label="Estimated Cost ($)"><Input type="number" min={0} /></Form.Item></Col>
            <Col span={6}><Form.Item name="actualCost" label="Actual Cost ($)"><Input type="number" min={0} /></Form.Item></Col>
          </Row>
        </Form>
      </Modal>

      {/* Detail Modal */}
      <Modal open={detailOpen} onCancel={() => setDetailOpen(false)} title={`Request ${selected?.requestNumber}`} width={720}
        footer={[
          <Button key="close" onClick={() => setDetailOpen(false)}>Close</Button>,
          selected && selected.status !== 'completed' && selected.status !== 'cancelled' && (
            <Button key="complete" type="primary" icon={<CheckCircleOutlined />} onClick={async () => { if (selected) { await handleStatusChange(selected, 'completed'); setDetailOpen(false); } }}>
              Mark Completed
            </Button>
          ),
        ]}
      >
        {selected && (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={8}><Statistic title="Unit" value={`Unit ${selected.unitNumber}`} /></Col>
              <Col span={8}><Statistic title="Resident" value={selected.ownerName} /></Col>
              <Col span={8}>
                <div><Text type="secondary">Status</Text></div>
                <Badge status={STATUS_CONFIG[selected.status].color as any} text={STATUS_CONFIG[selected.status].label} />
              </Col>
            </Row>
            <Divider />
            <Paragraph><strong>Title:</strong> {selected.title}</Paragraph>
            {/* Sprint 38 H4 — same Title Case normalisation as the column. */}
            <Paragraph><strong>Category:</strong> {selected.category
              ? selected.category.toLowerCase().replace(/(^|[_\s-])(\w)/g, (_, sep, c) => (sep ? ' ' : '') + c.toUpperCase())
              : '—'} &nbsp;
              <Tag color={PRIORITY_CONFIG[selected.priority].color}>{PRIORITY_CONFIG[selected.priority].label} Priority</Tag>
            </Paragraph>
            <Paragraph><strong>Description:</strong> {selected.description}</Paragraph>
            {selected.assignedVendor && <Paragraph><strong>Assigned to:</strong> {selected.assignedVendor}</Paragraph>}
            {selected.estimatedCost && <Paragraph><strong>Estimated cost:</strong> ${selected.estimatedCost}</Paragraph>}
            {selected.actualCost && <Paragraph><strong>Actual cost:</strong> ${selected.actualCost}</Paragraph>}
            {selected.completedDate && <Alert message={`Completed on ${dayjs(selected.completedDate).format('MMM D, YYYY HH:mm')}`} type="success" showIcon style={{ marginBottom: 12 }} />}

            <Divider plain>Quick Status Update</Divider>
            <Space wrap>
              {(Object.keys(STATUS_CONFIG) as RequestStatus[]).map(s => (
                <Button key={s} size="small" type={selected.status === s ? 'primary' : 'default'} onClick={() => handleStatusChange(selected, s)}>
                  {STATUS_CONFIG[s].label}
                </Button>
              ))}
            </Space>

            <Divider plain>Activity Notes</Divider>
            {selected.notes.length === 0 ? <Text type="secondary">No notes yet.</Text> : (
              <Timeline>
                {selected.notes.map(n => (
                  <Timeline.Item key={n.id} label={dayjs(n.date).format('MMM D HH:mm')}>
                    <Text strong>{n.author}: </Text>{n.text}
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
            <Space.Compact style={{ width: '100%', marginTop: 8 }}>
              <Input placeholder="Add a note…" value={noteText} onChange={e => setNoteText(e.target.value)} onPressEnter={() => handleAddNote(selected)} />
              <Button type="primary" onClick={() => handleAddNote(selected)}>Add</Button>
            </Space.Compact>
          </>
        )}
      </Modal>

      {/* Escalate to Work Order Modal */}
      <Modal
        open={escalateOpen}
        onCancel={() => { setEscalateOpen(false); escalateForm.resetFields(); }}
        title={
          <Space>
            <FileAddOutlined style={{ color: '#722ed1' }} />
            Escalate to Work Order
            {escalatingRequest && <Tag color="cyan">{escalatingRequest.requestNumber}</Tag>}
          </Space>
        }
        onOk={handleEscalateSubmit}
        okText="Create Work Order"
        okButtonProps={{ style: { background: '#722ed1', borderColor: '#722ed1' } }}
        width={620}
      >
        <Alert
          message="A new Work Order will be created and linked to this maintenance request."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={escalateForm} layout="vertical">
          <Form.Item name="title" label="Work Order Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="unitNumber" label="Unit Number" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="priority" label="Priority" rules={[{ required: true }]}>
                <Select>
                  <Select.Option value="LOW"><Tag color="green">Low</Tag></Select.Option>
                  <Select.Option value="MEDIUM"><Tag color="blue">Medium</Tag></Select.Option>
                  <Select.Option value="HIGH"><Tag color="orange">High</Tag></Select.Option>
                  <Select.Option value="URGENT"><Tag color="red">Urgent</Tag></Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Divider plain>Vendor & Scheduling (optional)</Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="vendorName" label="Vendor Name">
                <Select showSearch allowClear placeholder="Select or type vendor" mode={undefined}>
                  {VENDORS.map(v => <Select.Option key={v} value={v}>{v}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="vendorContact" label="Vendor Contact">
                <Input placeholder="Phone or email" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="scheduledDate" label="Scheduled Date">
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="estimatedCost" label="Estimated Cost ($)">
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} prefix="$" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Additional instructions for the work order…" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MaintenanceRequests;
